import { NIGHTMARES, PLAN, PLAN_TARGETS, PLANS, STRUCTURES, type PlanMove, type PlanTarget } from '@somnhold/content';
import { cellCenter, cellHex, cellId } from '../grid.ts';
import { hexDist, hexLen, hexRing, hexToWorld, type Hex } from '../hex.ts';
import { isInside } from '../walls.ts';
import { dist2, idiv, imax, imin, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, PlanParams, PlanState } from '../types.ts';
import { cellIdAt, ev } from './combat.ts';

/*
 * Battle plans (docs/TACTICAS.md). Each sector walks its plan's phases: a move leads out of a phase, made by
 * the general (the heuristic below, or a decision model through an `order` command) or by a trigger (a
 * breach, or the phase's time running out). Squads only change orders; what an order makes a nightmare do
 * lives in enemies.ts.
 */

/** Who sends the enemy general's moves in a battle's inputs. */
export const GENERAL_PID = 'general';

/**
 * The plan as the battle starts: holding squads wait, guards escort, the rest walks in. Without fronts, a
 * sector whose bulk has a strike cell of its own can only take that one, as either flank.
 */
export const newPlanState = (s: BattleState, p: PlanParams): PlanState => {
  const R = s.map.radius;
  const at = (h: Hex): number => cellId(R, h.q, h.r);
  return {
    id: p.id,
    auto: p.auto !== false,
    squads: p.squads.map((q) => ({
      role: q.role,
      sector: q.sector,
      stage: at(q.stage),
      strike: q.strike ? at(q.strike) : -1,
      order: q.role === 'main' || q.role === 'second' || q.role === 'wings' ? 'hold' : q.role === 'guard' ? 'escort' : 'advance',
      ground: q.sector,
      way: [],
    })),
    fronts: s.map.sectors.map((_, i) => {
      const f = p.fronts?.[i];
      if (f) return { stage: at(f.stage), left: at(f.left), right: at(f.right) };
      const own = p.squads.find((q) => q.sector === i && q.role === 'main' && q.strike);
      return own?.strike ? { stage: at(own.stage), left: at(own.strike), right: at(own.strike) } : { stage: -1, left: -1, right: -1 };
    }),
    shifted: s.map.sectors.map(() => false),
    lastPulse: s.map.sectors.map(() => -PLAN.pulseEvery),
    spots: wallSpots(s),
    phase: s.map.sectors.map(() => 0),
    since: s.map.sectors.map(() => 0),
    moves: [],
  };
};

/** The move that leads out of a sector's current phase, if any (none in a sector without squads). */
export const legalMove = (s: BattleState, sector: number): PlanMove | null => {
  const p = s.plan;
  if (!p || !p.squads.some((q) => q.sector === sector)) return null;
  return PLANS[p.id].phases[p.phase[sector] ?? 0]?.move ?? null;
};

/**
 * A nightmare joining a squad already charging heads for the strike cell too (in a siege's assault, unless it
 * has a flank of its own to storm).
 */
export const joinSquad = (s: BattleState, e: Enemy): void => {
  const q = s.plan?.squads[e.squad as number];
  if (!q || q.order !== 'charge' || q.strike < 0) return;
  if (s.plan?.id === 'escortedSiege' && e.rally !== undefined) return;
  e.rally = q.strike;
  if (s.plan?.spots.includes(q.strike)) e.spot = q.strike;
};

/** The wall spots: `PLAN.spots` cells evenly round the ring just outside the wall. */
const wallSpots = (s: BattleState): number[] => {
  // Terraced land: along the ground just outside the walls the group raised (none without walls).
  const ring = s.map.land ? (s.map.land.skirt ?? []) : hexRing(s.map.bastion + 1);
  if (ring.length === 0) return [];
  const out: number[] = [];
  for (let k = 0; k < PLAN.spots; k++) {
    const h = ring[idiv(k * ring.length, PLAN.spots)] as Hex;
    out.push(cellId(s.map.radius, h.q, h.r));
  }
  return out;
};

/** The sector a sector's bulk stands by: its own, or the one it shifted to. */
const groundOf = (s: BattleState, sector: number): number => s.plan?.squads.find((q) => q.sector === sector && q.role === 'main')?.ground ?? sector;

/** The cell a target is on a sector's ground: -1 straight in by the gate, null when it can't be taken there. */
const targetCell = (s: BattleState, ground: number, t: PlanTarget): number | null => {
  if (t === 'gate') return -1;
  if (t === 'breach') {
    const b = breachOf(s, ground);
    return b >= 0 ? b : null;
  }
  const f = s.plan?.fronts[ground];
  const c = f ? (t === 'left' ? f.left : f.right) : -1;
  return c >= 0 ? c : null;
};

/** Where a target is on the field (the gate's own cell for straight in). */
const targetPoint = (s: BattleState, ground: number, t: PlanTarget): { x: number; y: number } => {
  const c = targetCell(s, ground, t) ?? -1;
  if (c >= 0) return cellCenter(s.map.radius, c);
  const g = s.map.sectors[ground]?.gate ?? { q: 0, r: 0 };
  return hexToWorld(g.q, g.r);
};

/**
 * The targets the sector's move may take now, in the heuristic's order (the bulk's own flank first, if it was
 * drawn one). Empty: the move has no choice (the flyers, a pincer's two flanks).
 */
export const legalTargets = (s: BattleState, sector: number): PlanTarget[] => {
  const p = s.plan;
  if (!p || !legalMove(s, sector)) return [];
  const list = PLANS[p.id].phases[p.phase[sector] ?? 0]?.targets ?? [];
  const g = groundOf(s, sector);
  const ok = list.filter((t) => targetCell(s, g, t) !== null);
  const main = p.squads.find((q) => q.sector === sector && q.role === 'main');
  const f = p.fronts[g];
  const own: PlanTarget | null = !main || !f || main.strike < 0 ? null : main.strike === f.left ? 'left' : main.strike === f.right ? 'right' : null;
  return own && ok.includes(own) ? [own, ...ok.filter((t) => t !== own)] : ok;
};

/** How much of the sector's bulk (and a pincer's second half) has come out: [due, all] spawns. */
export const bulkOut = (s: BattleState, sector: number): [number, number] => {
  const p = s.plan;
  if (!p) return [0, 0];
  let due = 0;
  let all = 0;
  s.wave.spawns.forEach((sp, i) => {
    const q = sp.squad === undefined ? undefined : p.squads[sp.squad];
    if (!q || q.sector !== sector || (q.role !== 'main' && q.role !== 'second')) return;
    all++;
    if (i < s.wave.next) due++;
  });
  return [due, all];
};

/**
 * Whether a live general may make the sector's move now (its `order` commands): the flyers any time; the bulk
 * once `PLAN.readyBp` of it is out (sending it before is no plan at all). The heuristic plays as it always has.
 */
export const moveReady = (s: BattleState, sector: number): boolean => {
  const move = legalMove(s, sector);
  if (!move) return false;
  if (move === 'wings') return true;
  const [due, all] = bulkOut(s, sector);
  return due * 10000 >= all * PLAN.readyBp;
};

/** The sectors the sector's waiting bulk may shift to now (once per sector, while it still waits). */
export const legalShifts = (s: BattleState, sector: number): number[] => {
  const p = s.plan;
  if (!p || !PLANS[p.id].shift || p.shifted[sector] || !legalMove(s, sector)) return [];
  // Terraced land has no ring to walk round by: the bulk stays by its own fissure.
  if (s.map.land) return [];
  const main = p.squads.find((q) => q.sector === sector && q.role === 'main');
  if (!main || main.order !== 'hold') return [];
  const out: number[] = [];
  p.fronts.forEach((f, i) => {
    if (i !== main.ground && f.stage >= 0) out.push(i);
  });
  return out;
};

/** The sector's bulk waiting now: alive, of a holding bulk squad, not still walking round (lowest id first). */
const waitingBulk = (s: BattleState, sector: number): Enemy[] => {
  const p = s.plan as PlanState;
  return s.enemies.filter((e) => {
    if (e.hp <= 0 || e.squad === undefined) return false;
    const q = p.squads[e.squad];
    return !!q && q.sector === sector && q.role === 'main' && q.order === 'hold' && (e.leg ?? 0) >= q.way.length;
  });
};

/** The targets a pulse may take from the sector now (none: no pulse yet, or too few waiting). */
export const legalPulses = (s: BattleState, sector: number): PlanTarget[] => {
  const p = s.plan;
  if (!p || s.waveT - (p.lastPulse[sector] ?? 0) < PLAN.pulseEvery) return [];
  if (waitingBulk(s, sector).length < PLAN.pulseMin) return [];
  const g = groundOf(s, sector);
  return PLAN_TARGETS.filter((t) => targetCell(s, g, t) !== null);
};

/** A share of the sector's waiting bulk leaves it to storm a target; the rest keeps waiting. */
const pulse = (s: BattleState, sector: number, target: PlanTarget | undefined, spot: number | undefined, by: 'general' | 'trigger'): boolean => {
  const p = s.plan as PlanState;
  const open = legalPulses(s, sector);
  if (!open.length) return false;
  if (spot !== undefined ? p.spots[spot] === undefined : target === undefined || !open.includes(target)) return false;
  const bulk = waitingBulk(s, sector);
  const n = idiv(bulk.length * PLAN.pulseShareBp + 9999, 10000);
  const cell = spot !== undefined ? (p.spots[spot] as number) : (targetCell(s, groundOf(s, sector), target as PlanTarget) ?? -1);
  for (const e of bulk.slice(0, n)) {
    e.squad = undefined;
    e.leg = undefined;
    if (cell >= 0) e.rally = cell;
    else e.rally = undefined;
    if (spot !== undefined) e.spot = cell;
  }
  p.lastPulse[sector] = s.waveT;
  const aim = spot !== undefined ? { spot } : { target };
  p.moves.push({ tick: s.tick, sector, move: 'pulse', by, ...aim });
  const at = horn(s, sector);
  ev(s, { t: 'plan', sector, move: 'pulse', ...aim, x: at.x, y: at.y });
  return true;
};

/**
 * The way round from one staging cell to another, outside the bastion: along the ring through the farther of
 * the two (and at least `PLAN.wayOut` past the wall), the shorter way, then the cell itself.
 */
const wayRound = (s: BattleState, from: number, to: number): number[] => {
  const R = s.map.radius;
  const a = cellHex(R, from);
  const b = cellHex(R, to);
  const rr = imin(R - 1, imax(s.map.bastion + PLAN.wayOut, imax(hexLen(a.q, a.r), hexLen(b.q, b.r))));
  const ring = hexRing(rr);
  const nearest = (h: Hex): number => {
    let best = 0;
    for (let i = 1; i < ring.length; i++) if (hexDist(ring[i] as Hex, h) < hexDist(ring[best] as Hex, h)) best = i;
    return best;
  };
  const ia = nearest(a);
  const ib = nearest(b);
  const n = ring.length;
  const fwd = (ib - ia + n) % n;
  const dir = fwd * 2 <= n ? 1 : -1;
  const steps = dir === 1 ? fwd : n - fwd;
  const out: number[] = [];
  for (let k = 0; k <= steps; k++) {
    const h = ring[(ia + dir * k + n) % n] as Hex;
    out.push(cellId(R, h.q, h.r));
  }
  out.push(to);
  return out;
};

/** The sector's waiting bulk walks round to another sector and waits there, its targets now that sector's. */
const shift = (s: BattleState, sector: number, to: number | undefined, by: 'general' | 'trigger'): boolean => {
  const p = s.plan as PlanState;
  if (to === undefined || !legalShifts(s, sector).includes(to)) return false;
  const dest = p.fronts[to] as PlanState['fronts'][number];
  p.squads.forEach((q, i) => {
    if (q.sector !== sector || q.role !== 'main') return;
    const f = p.fronts[q.ground];
    q.strike = f && q.strike >= 0 ? (q.strike === f.left ? dest.left : q.strike === f.right ? dest.right : -1) : -1;
    q.way = wayRound(s, q.stage, dest.stage);
    q.stage = dest.stage;
    q.ground = to;
    for (const e of s.enemies) if (e.squad === i && e.hp > 0) e.leg = 0;
  });
  p.shifted[sector] = true;
  p.moves.push({ tick: s.tick, sector, move: 'shift', by, to });
  const at = horn(s, sector);
  ev(s, { t: 'plan', sector, move: 'shift', to, x: at.x, y: at.y });
  return true;
};

/**
 * Makes a move in a sector (when it is the legal one there, at a legal target): the squads it sends get their
 * new orders. Without a target it takes the heuristic's.
 */
export const makeMove = (s: BattleState, sector: number, move: PlanMove, by: 'general' | 'trigger', aim: { target?: PlanTarget; spot?: number; to?: number } = {}): boolean => {
  const p = s.plan;
  if (move === 'shift') return !!p && shift(s, sector, aim.to, by);
  if (move === 'pulse') return !!p && pulse(s, sector, aim.target, aim.spot, by);
  if (!p || legalMove(s, sector) !== move) return false;
  const targets = legalTargets(s, sector);
  // A wall spot, for a move that takes a target (not the flyers, not a pincer's two flanks).
  const spot = aim.spot !== undefined && targets.length && p.spots[aim.spot] !== undefined ? aim.spot : undefined;
  if (aim.spot !== undefined && spot === undefined) return false;
  const target = targets.length && spot === undefined ? (aim.target ?? heuristicTarget(s, sector)) : undefined;
  if (target !== undefined && !targets.includes(target)) return false;
  const send = (role: 'main' | 'second' | 'wings', strike: number): void => {
    p.squads.forEach((q, i) => {
      if (q.sector !== sector || q.role !== role) return;
      q.strike = strike;
      q.order = strike >= 0 ? 'charge' : 'advance';
      for (const e of s.enemies) if (e.squad === i && e.hp > 0) joinSquad(s, e);
    });
  };
  const aimed = spot !== undefined ? (p.spots[spot] as number) : target === undefined ? null : (targetCell(s, groundOf(s, sector), target) ?? -1);
  if (move === 'assault') send('main', aimed ?? breachOf(s, sector));
  else if (move === 'wings') send('wings', -1);
  else {
    // At its target; else each half to its own strike cell (a pincer's two flanks).
    for (const role of ['main', 'second'] as const) send(role, aimed ?? p.squads.find((q) => q.sector === sector && q.role === role)?.strike ?? -1);
    send('wings', -1);
  }
  p.phase[sector] = (p.phase[sector] ?? 0) + 1;
  p.since[sector] = s.waveT;
  const aim2 = spot !== undefined ? { spot } : target ? { target } : {};
  p.moves.push({ tick: s.tick, sector, move, by, ...aim2 });
  const at = horn(s, sector);
  ev(s, { t: 'plan', sector, move, ...aim2, x: at.x, y: at.y });
  return true;
};

/** The heuristic's target for the sector's move: a breach if there is one, else the first no hero watches. */
export const heuristicTarget = (s: BattleState, sector: number): PlanTarget | undefined => {
  const opts = legalTargets(s, sector);
  if (!opts.length) return undefined;
  if (opts.includes('breach')) return 'breach';
  const g = groundOf(s, sector);
  const heroes = s.heroes.filter(alive);
  for (const t of opts) {
    const c = targetPoint(s, g, t);
    if (!heroes.some((h) => withinRange(h.x, h.y, c.x, c.y, PLAN.watchRadius))) return t;
  }
  return opts[0];
};

/**
 * The heuristic's shift: when most of the heroes stand by the sector's gate and none by another's, the
 * waiting bulk goes round to that one (the lowest).
 */
export const heuristicShift = (s: BattleState, sector: number): number | null => {
  const opts = legalShifts(s, sector);
  if (!opts.length) return null;
  const heroes = s.heroes.filter(alive);
  if (!heroes.length) return null;
  const by = (i: number): number => {
    const g = s.map.sectors[i]?.gate ?? { q: 0, r: 0 };
    const c = hexToWorld(g.q, g.r);
    return heroes.filter((h) => withinRange(h.x, h.y, c.x, c.y, PLAN.awayRadius)).length;
  };
  const p = s.plan as PlanState;
  const ph = PLANS[p.id].phases[p.phase[sector] ?? 0];
  if (!ph || ph.by - s.waveT < PLAN.shiftLead) return null;
  if (by(sector) * 10000 < heroes.length * PLAN.shiftShareBp) return null;
  for (const to of opts) if (by(to) === 0) return to;
  return null;
};

/** Where the sector's horn sounds: where its bulk waits, else its gate. */
const horn = (s: BattleState, sector: number): { x: number; y: number } => {
  const q = s.plan?.squads.find((x) => x.sector === sector && x.role === 'main');
  if (q && q.stage >= 0) return cellCenter(s.map.radius, q.stage);
  const g = s.map.sectors[sector]?.gate ?? { q: 0, r: 0 };
  return hexToWorld(g.q, g.r);
};

/** The sector a cell belongs to: the one whose gate is nearest. */
const sectorOfCell = (s: BattleState, q: number, r: number): number => {
  let best = 0;
  let bd = 0x7fffffff;
  s.map.sectors.forEach((sec, i) => {
    const d = hexDist({ q, r }, sec.gate);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
};

/** A wall, gate or rampart of the sector fallen (the nearest to its gate): its cell, or -1. */
export const breachOf = (s: BattleState, sector: number): number => {
  const gate = s.map.sectors[sector]?.gate;
  if (!gate) return -1;
  let best = -1;
  let bd = 0x7fffffff;
  for (const st of s.structures) {
    if (!st.destroyed || STRUCTURES[st.type].category !== 'defense' || sectorOfCell(s, st.q, st.r) !== sector) continue;
    const d = hexDist({ q: st.q, r: st.r }, gate);
    if (d < bd) {
      bd = d;
      best = cellId(s.map.radius, st.q, st.r);
    }
  }
  return best;
};

const alive = (h: Hero): boolean => h.downTicks === 0 && h.hp > 0;

/** The squad's members on the field and where they stand on average (null with none). */
const squadAt = (s: BattleState, sector: number, role: string): { n: number; x: number; y: number } | null => {
  const p = s.plan as PlanState;
  let n = 0;
  let x = 0;
  let y = 0;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.squad === undefined) continue;
    const q = p.squads[e.squad];
    if (!q || q.sector !== sector || q.role !== role) continue;
    n++;
    x += e.x;
    y += e.y;
  }
  return n ? { n, x: idiv(x, n), y: idiv(y, n) } : null;
};

/** Whether the sector's squad of that role has come out at all (its spawns are due). */
const spawned = (s: BattleState, sector: number, role: string): boolean => {
  const p = s.plan as PlanState;
  const w = s.wave;
  for (let i = 0; i < w.next; i++) {
    const sp = w.spawns[i];
    if (sp?.squad === undefined) continue;
    const q = p.squads[sp.squad];
    if (q && q.sector === sector && q.role === role) return true;
  }
  return false;
};

/** Out past the wall: no walled cell within `PLAN.sortieCells` of where it stands (a hero at the gate isn't out). */
/** Whether some of the sector's squad of that role is still to come (spawns not yet due). */
const pending = (s: BattleState, sector: number, role: string): boolean => {
  const p = s.plan as PlanState;
  const w = s.wave;
  for (let i = w.next; i < w.spawns.length; i++) {
    const sp = w.spawns[i];
    if (sp?.squad === undefined) continue;
    const q = p.squads[sp.squad];
    if (q && q.sector === sector && q.role === role) return true;
  }
  return false;
};

const outPast = (s: BattleState, x: number, y: number): boolean => {
  const c = cellIdAt(s, x, y);
  if (c < 0) return true;
  const h = cellHex(s.map.radius, c);
  const n = PLAN.sortieCells;
  for (let dq = -n; dq <= n; dq++) {
    for (let dr = imax(-n, -dq - n); dr <= imin(n, -dq + n); dr++) if (isInside(s.map, h.q + dq, h.r + dr)) return false;
  }
  return true;
};

/** Whether the sector's shifted bulk hasn't got there yet: none of it there, or some still walking round. */
const onTheWay = (s: BattleState, sector: number): boolean => {
  const p = s.plan as PlanState;
  if (!p.shifted[sector]) return false;
  let there = false;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.squad === undefined) continue;
    const q = p.squads[e.squad];
    if (!q || q.sector !== sector || q.role !== 'main') continue;
    if ((e.leg ?? 0) < q.way.length) return true;
    there = true;
  }
  return !there;
};

/**
 * The heuristic general: the move it would make in a sector now, or null to hold. It reads what the heroes
 * do: out after the engines leaves the gate bare; at the breach, the sky is open; on the decoy, the flank.
 */
export const heuristicMove = (s: BattleState, sector: number): PlanMove | null => {
  const move = legalMove(s, sector);
  if (!move) return null;
  const p = s.plan as PlanState;
  // A bulk still on its way round strikes from where it is going, not from halfway.
  if (move !== 'wings' && onTheWay(s, sector)) return null;
  const heroes = s.heroes.filter(alive);
  if (move === 'assault') {
    const engines = squadAt(s, sector, 'engines');
    // The engines and their guard are gone: no point waiting.
    if (!engines && !squadAt(s, sector, 'guard') && spawned(s, sector, 'engines')) return move;
    if (!engines || heroes.length === 0) return null;
    const out = heroes.filter((h) => withinRange(h.x, h.y, engines.x, engines.y, PLAN.guardRadius * 2) && outPast(s, h.x, h.y)).length;
    return out * 10000 >= heroes.length * PLAN.sortieShareBp ? move : null;
  }
  if (move === 'wings') {
    const strike = p.squads.find((q) => q.sector === sector && q.role === 'main')?.strike ?? -1;
    if (s.waveT - (p.since[sector] ?? 0) >= PLAN.wingsAfter) return move;
    if (strike < 0) return null;
    const c = cellCenter(s.map.radius, strike);
    return heroes.some((h) => withinRange(h.x, h.y, c.x, c.y, PLAN.watchRadius)) ? move : null;
  }
  if (p.id === 'rush') {
    // All gathered (nothing of the bulk still to come), or nobody minding this side's gate.
    if (!pending(s, sector, 'main')) return move;
    const gate = s.map.sectors[sector]?.gate;
    if (!gate || heroes.length === 0) return null;
    const g = hexToWorld(gate.q, gate.r);
    return heroes.some((h) => withinRange(h.x, h.y, g.x, g.y, PLAN.awayRadius)) ? null : move;
  }
  if (p.id === 'pincer') {
    // Both halves out, and the heroes bunched where they can hold one flank at most.
    if (pending(s, sector, 'main') || pending(s, sector, 'second') || heroes.length === 0) return null;
    const a = heroes[0] as Hero;
    return heroes.every((h) => withinRange(h.x, h.y, a.x, a.y, PLAN.bunchRadius)) ? move : null;
  }
  // strike (feint): the heroes are on the decoy and nobody watches the flank (or the decoy is spent).
  const decoy = squadAt(s, sector, 'decoy');
  if (!decoy) return spawned(s, sector, 'decoy') ? move : null;
  const main = p.squads.find((q) => q.sector === sector && q.role === 'main');
  if (!main || main.strike < 0) return null;
  const c = cellCenter(s.map.radius, main.strike);
  const onBait = heroes.some((h) => withinRange(h.x, h.y, decoy.x, decoy.y, PLAN.baitRadius));
  const watched = heroes.some((h) => withinRange(h.x, h.y, c.x, c.y, PLAN.watchRadius));
  return onBait && !watched ? move : null;
};

/**
 * Whether a hero came out to the sector's waiting squads: one out past the wall within `PLAN.provokeRadius`
 * of a nightmare that waits. Nobody stands there to be hit: the move goes at once (the bare gate, the flank).
 */
const provoked = (s: BattleState, sector: number): boolean => {
  const p = s.plan as PlanState;
  const heroes = s.heroes.filter((h) => alive(h) && outPast(s, h.x, h.y));
  if (!heroes.length) return false;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.squad === undefined) continue;
    const q = p.squads[e.squad];
    if (!q || q.sector !== sector || q.order !== 'hold') continue;
    if (heroes.some((h) => withinRange(h.x, h.y, e.x, e.y, PLAN.provokeRadius))) return true;
  }
  return false;
};

/** Every tick: triggers (a breach, time) move the plan on; every few, the heuristic general looks. */
export const planTick = (s: BattleState): void => {
  const p = s.plan;
  if (!p) return;
  const def = PLANS[p.id];
  for (let sector = 0; sector < p.phase.length; sector++) {
    const move = legalMove(s, sector);
    if (!move) continue;
    const ph = def.phases[p.phase[sector] as number];
    if (ph && s.waveT >= ph.by) {
      makeMove(s, sector, move, 'trigger');
      continue;
    }
    if (move === 'assault' && breachOf(s, groundOf(s, sector)) >= 0) {
      makeMove(s, sector, move, 'trigger');
      continue;
    }
    if (p.auto && s.waveT % PLAN.every === 0) {
      const to = heuristicShift(s, sector);
      if (to !== null) {
        makeMove(s, sector, 'shift', 'general', { to });
        continue;
      }
      const m = heuristicMove(s, sector);
      if (m && makeMove(s, sector, m, 'general')) continue;
    }
    if (s.waveT % PLAN.every === 0 && provoked(s, sector)) makeMove(s, sector, move, 'trigger');
  }
  // A guard whose engines are all gone has nothing to guard: it walks in.
  p.squads.forEach((q) => {
    if (q.role !== 'guard' || q.order !== 'escort') return;
    if (spawned(s, q.sector, 'engines') && !squadAt(s, q.sector, 'engines')) q.order = 'advance';
  });
};

/** The engine a guard keeps by: the nearest alive one of its sector's engines. */
export const engineFor = (s: BattleState, e: Enemy): Enemy | null => {
  const p = s.plan as PlanState;
  const sector = p.squads[e.squad as number]?.sector;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const o of s.enemies) {
    if (o.hp <= 0 || o.squad === undefined || !NIGHTMARES[o.kind].siege) continue;
    const q = p.squads[o.squad];
    if (!q || q.role !== 'engines' || q.sector !== sector) continue;
    const d = dist2(e.x, e.y, o.x, o.y);
    if (d < bd) {
      bd = d;
      best = o;
    }
  }
  return best;
};
